import i18n from 'i18next';

import { mapRateSourceFromDTO } from '@/modules/common/mappers/rate-source.mapper';
import { RateSourceDTO } from '@/modules/common/types/dto/rate-source.dto';
import { getRateSourceLabel } from '@/modules/common/utils/rate-source.utils';
import { mapProjectBillingModelFromDTO } from '@/modules/projects/mappers/project-billing-model.mapper';
import { ProjectBillingModelDTO } from '@/modules/projects/types/dto/project-billing-model.dto';
import { ProjectBillingModel } from '@/modules/projects/types/project';
import {
  ConflictFieldConfig,
  ConflictFieldGroup,
  ConflictSnapshot,
  SubRowData,
} from '@/modules/projects/types/project-conflicts';
import { ProjectTeamMember } from '@/modules/projects/types/project-team-member';
import {
  extractCurrencyCode,
  getBillingModelLabel,
  getMemberRateBySource,
} from '@/modules/projects/utils';

/**
 * Field keys for project billing conflict resolution
 */
export type ProjectBillingFieldKey =
  | 'billingModel'
  | 'currency'
  | 'rateSource'
  | 'flatRate'
  | 'projectBudget'
  | 'userRates';

/**
 * Parent field keys that have sub-rows
 */
export enum ProjectBillingParentFieldKey {
  UserRates = 'userRates',
}

/**
 * Maps billing model value from either DTO or app enum to app enum for comparison
 * Handles both API response format (TIME_AND_MATERIALS) and form format (TM)
 */
const normalizeBillingModel = (value: string | null | undefined): ProjectBillingModel | null => {
  if (!value) return null;

  // Check if it's already an app enum value
  if (Object.values(ProjectBillingModel).includes(value as ProjectBillingModel)) {
    return value as ProjectBillingModel;
  }

  return mapProjectBillingModelFromDTO(value as ProjectBillingModelDTO);
};

/**
 * Normalizes rate source value from either DTO or app enum for comparison
 * Returns the DTO format for consistent comparison
 */
const normalizeRateSource = (value: string | null | undefined): RateSourceDTO | null => {
  if (!value) return null;

  // Convert to uppercase for consistent comparison
  const normalized = String(value).toUpperCase();

  // Check if it's a valid DTO value
  if (Object.values(RateSourceDTO).includes(normalized as RateSourceDTO)) {
    return normalized as RateSourceDTO;
  }

  return null;
};

/**
 * Wrapper for getRateSourceLabel that accepts RateSourceDTO
 * Converts DTO to domain model and uses the shared label utility
 */
const getRateSourceLabelFromDTO = (value: RateSourceDTO | null): string => {
  if (!value) return i18n.t('common:table.noValue');
  const rateSource = mapRateSourceFromDTO(value);
  return getRateSourceLabel(rateSource);
};

/**
 * Converts a team member to a rate value object with normalized rate source
 */
const getMemberRateValue = (
  member: ProjectTeamMember | undefined
): { rateSource: RateSourceDTO; rate: number } | null => {
  if (!member) return null;

  return {
    rateSource: normalizeRateSource(member.activeRateSource) ?? RateSourceDTO.USER,
    rate: getMemberRateBySource(member),
  };
};

/**
 * Converts member data to comparison format (id + rate source + rate)
 */
const mapMemberToComparisonFormat = (member: ProjectTeamMember) => ({
  id: member.id,
  rateSource: normalizeRateSource(member.activeRateSource),
  rate: getMemberRateBySource(member),
});

/**
 * Default currency code used when currency context is not available
 */
const DEFAULT_CURRENCY_CODE = 'USD';

/**
 * Field configurations for project billing conflict resolution
 * Defines how each field should be displayed in the conflicts table
 */
export const projectBillingConflictFields: ConflictFieldConfig<ProjectBillingFieldKey>[] = [
  {
    key: 'billingModel',
    labelKey: 'conflictFields.billing.billingModel',
    valueFormatter: value => getBillingModelLabel(normalizeBillingModel(value)),
    getFormValueForComparison: normalizeBillingModel,
    getSnapshotValueForComparison: normalizeBillingModel,
  },
  {
    key: 'currency',
    snapshotKey: 'defaultCurrency',
    labelKey: 'conflictFields.billing.currency',
    valueFormatter: value => extractCurrencyCode(value) ?? i18n.t('common:table.noValue'),
    getFormValueForComparison: extractCurrencyCode,
    getSnapshotValueForComparison: extractCurrencyCode,
  },
  {
    key: 'rateSource',
    labelKey: 'conflictFields.billing.rateSource',
    valueFormatter: value => getRateSourceLabelFromDTO(normalizeRateSource(value)),
    getFormValueForComparison: normalizeRateSource,
    getSnapshotValueForComparison: normalizeRateSource,
    shouldShow: selectedValues =>
      normalizeBillingModel(selectedValues.billingModel) !== ProjectBillingModel.FixedPrice,
    isDisabled: selectedValues =>
      normalizeBillingModel(selectedValues.billingModel) === ProjectBillingModel.NonBillable,
    showAlongsideField: 'flatRate',
  },
  {
    key: 'flatRate',
    labelKey: 'conflictFields.billing.flatRate',
    valueFormatter: (value, context) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      const currency = context?.currency?.code || context?.currency || DEFAULT_CURRENCY_CODE;
      return `${value} ${currency}`;
    },
    shouldShow: selectedValues => {
      return (
        normalizeBillingModel(selectedValues.billingModel) !== ProjectBillingModel.FixedPrice &&
        normalizeRateSource(selectedValues.rateSource) === RateSourceDTO.FLAT
      );
    },
    isDisabled: selectedValues =>
      normalizeBillingModel(selectedValues.billingModel) === ProjectBillingModel.NonBillable,
  },
  {
    key: 'projectBudget',
    snapshotKey: 'budget',
    labelKey: 'conflictFields.billing.projectBudget',
    valueFormatter: (value, context) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      const currency = context?.currency?.code || context?.currency || DEFAULT_CURRENCY_CODE;
      return `${value} ${currency}`;
    },
  },
  {
    key: 'userRates',
    labelKey: 'conflictFields.billing.userRates',
    hasSubRows: true,
    shouldShow: selectedValues =>
      normalizeBillingModel(selectedValues.billingModel) !== ProjectBillingModel.FixedPrice,

    valueFormatter: (value: { rateSource: RateSourceDTO; rate: number } | null) => {
      if (value === null || value === undefined) {
        return i18n.t('common:table.noValue');
      }
      return getRateSourceLabelFromDTO(value.rateSource);
    },

    getSubRows: (
      formValue: ProjectTeamMember[] | null,
      snapshots: ConflictSnapshot[]
    ): SubRowData[] => {
      const membersMap = new Map<string, SubRowData>();

      // Process form members
      if (formValue && Array.isArray(formValue)) {
        formValue.forEach(member => {
          membersMap.set(member.id, {
            id: member.id,
            label: member.name,
            avatarUrl: member.avatarLink,
            currentValue: getMemberRateValue(member),
            snapshotValues: new Map(),
          });
        });
      }

      // Process snapshot members
      // Note: Snapshot members may have different structure with nested user object
      snapshots.forEach(snapshot => {
        const snapshotMembers = snapshot.data.userRates as unknown[];
        if (snapshotMembers && Array.isArray(snapshotMembers)) {
          snapshotMembers.forEach((memberData: any) => {
            // Snapshot structure: { user: {...}, ...member props } or just member props
            const member = memberData as ProjectTeamMember;
            const userId = String(memberData.user?.id ?? member.id);
            const userName = memberData.user?.name ?? member.name;
            const userAvatar = memberData.user?.profileImageUrl ?? member.avatarLink;
            const rate = getMemberRateValue(member);

            if (!membersMap.has(userId)) {
              // New member from snapshot
              membersMap.set(userId, {
                id: userId,
                label: userName,
                avatarUrl: userAvatar,
                currentValue: null,
                snapshotValues: new Map([[snapshot.id, rate]]),
              });
            } else {
              // Existing member - add snapshot value
              membersMap.get(userId)?.snapshotValues.set(snapshot.id, rate);
            }
          });
        }
      });

      // For members in form but not in snapshot, set null for that snapshot
      membersMap.forEach(row => {
        snapshots.forEach(snapshot => {
          if (!row.snapshotValues.has(snapshot.id)) {
            row.snapshotValues.set(snapshot.id, null);
          }
        });
      });

      return Array.from(membersMap.values());
    },

    getFormValueForComparison: value => {
      if (!value || !Array.isArray(value)) return [];
      return value.map(mapMemberToComparisonFormat);
    },

    getSnapshotValueForComparison: value => {
      if (!value || !Array.isArray(value)) return [];
      return value.map(mapMemberToComparisonFormat);
    },
  },
];

/**
 * Grouped fields configuration for billing conflicts
 * All fields must be included in groups when groupedFields is used,
 * otherwise ungrouped fields won't be rendered in the table
 */
export const projectBillingGroupedFields: ConflictFieldGroup<ProjectBillingFieldKey>[] = [
  {
    headerLabelKey: 'authenticated.projects:conflictFields.billing.configurationHeader',
    fields: ['billingModel', 'currency', 'rateSource', 'flatRate', 'projectBudget'],
  },
  {
    headerLabelKey: 'authenticated.projects:conflictFields.billing.userRatesHeader',
    fields: ['userRates'],
  },
];
